import { ArrowUp } from 'lucide-react'
import { useEffect, useState } from 'react'

import { cn } from '@/theme/utils'

interface ScrollToTopProps {
  threshold?: number
}

const SELECTOR_SCROLLABLE_CONTAINER = '.overflow-y-auto'

export default function ScrollToTop({ threshold = 300 }: ScrollToTopProps) {
  const [isVisible, setIsVisible] = useState(false)

  useEffect(() => {
    const scrollContainer = document.querySelector(
      SELECTOR_SCROLLABLE_CONTAINER,
    )

    if (!scrollContainer) return

    const toggleVisibility = () => {
      const currentScrollTop =
        scrollContainer.scrollTop ?? document.documentElement.scrollTop
      setIsVisible(currentScrollTop > threshold)
    }

    toggleVisibility()
    scrollContainer.addEventListener('scroll', toggleVisibility, {
      passive: true,
    })
    return () => scrollContainer.removeEventListener('scroll', toggleVisibility)
  }, [threshold])

  const scrollToTop = () => {
    const scrollContainer = document.querySelector(
      SELECTOR_SCROLLABLE_CONTAINER,
    )

    if (!scrollContainer) {
      window.scrollTo({ top: 0, behavior: 'smooth' })
      return
    }

    scrollContainer?.scrollTo({ top: 0, behavior: 'smooth' })
  }

  return (
    <button
      onClick={scrollToTop}
      className={cn(
        'fixed right-8 bottom-8',
        'h-12 w-12 rounded-full',
        'bg-primary hover:bg-primary/90',
        'text-primary-foreground',
        'shadow-lg',
        'flex items-center justify-center',
        'transform transition-all duration-300 ease-in-out',
        isVisible
          ? 'pointer-events-auto translate-x-0 opacity-100'
          : 'pointer-events-none translate-x-6 opacity-0',
      )}
      tabIndex={isVisible ? 0 : -1}
      aria-label='Scroll to top'
      style={{ zIndex: 9999 }}
    >
      <ArrowUp className='h-5 w-5' />
    </button>
  )
}
